interface AnalyticsPage {
    url: string;
    props: {
        [key: string]: unknown;
        auth?: { user?: unknown };
        analytics?: { distinctId: string | null };
    };
}

interface AnalyticsPayload {
    [key: string]: unknown;
}

interface Umami {
    track: (payload: (defaults: AnalyticsPayload) => AnalyticsPayload) => void | Promise<unknown>;
    identify: (id: string) => void | Promise<unknown>;
}

interface AnalyticsWindow extends Window {
    umami?: Umami;
    poddtoppenAccountBeforeSend?: (
        type: string,
        payload: AnalyticsPayload,
    ) => AnalyticsPayload | null;
}

type AccountEvent =
    | 'account-play-click'
    | 'account-podcast-unfollow'
    | 'account-episode-unsave'
    | 'account-episode-mark-played'
    | 'account-episode-mark-unplayed';

let accountPath: string | null = null;
let accountDistinctId: string | null = null;
let identifiedId: string | null = null;
let lastTrackedPath: string | null = null;

function synchronizeIdentity(): void {
    if (typeof window === 'undefined') return;
    const analyticsWindow = window as AnalyticsWindow;
    analyticsWindow.poddtoppenAccountBeforeSend = (type, payload) => {
        if (!accountPath) return null;
        return type === 'identify'
            ? { ...payload, url: accountPath, referrer: '', title: 'Mina sidor' }
            : payload;
    };

    const umami = analyticsWindow.umami;
    if (!umami || identifiedId === accountDistinctId) return;

    try {
        void Promise.resolve(umami.identify(accountDistinctId ?? '')).catch(() => undefined);
        identifiedId = accountDistinctId;
    } catch {
        // Tracking must not interrupt account navigation.
    }
}

function send(name?: AccountEvent, data: Record<string, string | number> = {}): boolean {
    if (typeof window === 'undefined' || !accountPath || !accountDistinctId) return false;
    synchronizeIdentity();
    const umami = (window as AnalyticsWindow).umami;
    if (!umami) return false;

    const path = accountPath;
    const distinctId = accountDistinctId;
    try {
        void Promise.resolve(
            umami.track((defaults) => ({
                ...defaults,
                id: distinctId ?? undefined,
                url: path,
                referrer: '',
                title: 'Mina sidor',
                ...(name ? { name } : {}),
                data: {
                    ...data,
                    area: 'account',
                    signed_in: true,
                    account_id: distinctId,
                    schema_version: 1,
                },
            })),
        ).catch(() => undefined);
        return true;
    } catch {
        return false;
    }
}

export function trackAccountEvent(
    event: AccountEvent,
    data: Record<string, string | number> = {},
): void {
    send(event, data);
}

export function trackAccountPage(page: AnalyticsPage): void {
    const path = page.url.split(/[?#]/, 1)[0];
    accountPath = page.props.auth?.user && /^\/mina-sidor(?:\/|$)/.test(path) ? path : null;
    const distinctId = accountPath ? (page.props.analytics?.distinctId ?? null) : null;
    if (distinctId !== accountDistinctId) lastTrackedPath = null;
    accountDistinctId = distinctId;
    synchronizeIdentity();
    if (!accountPath) {
        lastTrackedPath = null;
        return;
    }
    if (accountPath !== lastTrackedPath && send()) lastTrackedPath = accountPath;
}

export function initializeAccountAnalytics(page: AnalyticsPage): void {
    trackAccountPage(page);
    window.addEventListener(
        'load',
        () => {
            if (accountPath !== lastTrackedPath && send()) lastTrackedPath = accountPath;
        },
        { once: true },
    );
}
